/**
 * Onboarding journey ("parcours utilisateur").
 *
 * `Journey` and `JourneyStep` mirror `gt-api/app/types/journey.ts` — keep both in sync.
 * Everything below them is derived from `steps` by `JourneyService` and never crosses the
 * wire.
 *
 * The API returns keys only. Labels live in the i18n bundles under
 * `help.onboarding.journey.*`, and the call-to-action targets in
 * `src/app/constants/journey-actions.ts`: routes and localized document links are client
 * concerns, so the backend has no business knowing about them.
 */

export const JOURNEY_PHASES = [
  'discover',
  'understand',
  'reduce',
  'engage',
  'review',
] as const;

export type JourneyPhase = (typeof JOURNEY_PHASES)[number];

/**
 * `user`    — learning steps, redone by every new colleague.
 * `company` — the carbon programme, done once and shared by the whole account.
 */
export type JourneyTrack = 'user' | 'company';

export interface JourneyStep {
  key: string;
  phase: JourneyPhase;
  track: JourneyTrack;
  completed: boolean;
  completedAt: string | null;
}

export interface Journey {
  /** Flat, in catalogue order. */
  steps: JourneyStep[];
  /** False once the user ticked "ne plus montrer": the popup never opens again. */
  showPopup: boolean;
}

export interface JourneyProgress {
  completed: number;
  total: number;
}

/** One phase of the journey, with everything the path and the accordion need to draw it. */
export interface JourneyPhaseView {
  phase: JourneyPhase;
  steps: JourneyStep[];
  /** 1-based position on the path. */
  position: number;
  completed: number;
  total: number;
  isComplete: boolean;
  /** First unfinished phase: the one the path highlights and the page opens by default. */
  isCurrent: boolean;
}
